import { lazy } from 'react';
import { Route, Routes } from 'react-router-dom';
import { NotFoundPage } from '../shared/components/NotFoundPage';

const DashboardPage = lazy(() => import('../features/dashboard/DashboardPage'));
const ExperienceCenterPage = lazy(() => import('../features/experiences/ExperienceCenterPage'));
const JournalsPage = lazy(() => import('../features/journals/JournalsPage'));
const JournalEditorPage = lazy(() => import('../features/journals/JournalEditorPage'));
const JournalDetailPage = lazy(() => import('../features/journals/JournalDetailPage'));
const LedgerPage = lazy(() => import('../features/ledger/LedgerPage'));
const AccountsPage = lazy(() => import('../features/accounts/AccountsPage'));
const PersonsPage = lazy(() => import('../features/persons/PersonsPage'));
const CustomersPage = lazy(() => import('../features/customers/CustomersPage'));
const SuppliersPage = lazy(() => import('../features/suppliers/SuppliersPage'));
const ExchangeRatesPage = lazy(() => import('../features/exchange/ExchangeRatesPage'));
const CurrencyAdministrationPage = lazy(() => import('../features/exchange/CurrencyAdministrationPage'));
const InventoryPage = lazy(() => import('../features/inventory/InventoryPage'));
const ReportsPage = lazy(() => import('../features/reports/ReportsPage'));
const ReceivablesPage = lazy(() => import('../features/receivables/ReceivablesPage'));
const PayablesPage = lazy(() => import('../features/payables/PayablesPage'));
const TreasuryPage = lazy(() => import('../features/treasury/TreasuryPage'));
const SalesPage = lazy(() => import('../features/sales/SalesPage'));
const PurchasingPage = lazy(() => import('../features/purchasing/PurchasingPage'));
const DocumentAutomationPage = lazy(() => import('../features/automation/DocumentAutomationPage'));
const PayrollPage = lazy(() => import('../features/payroll/PayrollPage'));
const WorkforcePage = lazy(() => import('../features/workforce/WorkforcePage'));
const TaxPage = lazy(() => import('../features/tax/TaxPage'));
const FixedAssetsPage = lazy(() => import('../features/fixedAssets/FixedAssetsPage'));
const BudgetingPage = lazy(() => import('../features/budgeting/BudgetingPage'));
const CostingPage = lazy(() => import('../features/costing/CostingPage'));
const ConstructionOperationsPage = lazy(() => import('../features/construction/ConstructionOperationsPage'));
const ProjectsPage = lazy(() => import('../features/projects/ProjectsPage'));
const PersonalFinancePage = lazy(() => import('../features/personalFinance/PersonalFinancePage'));
const SettingsPage = lazy(() => import('../features/settings/SettingsPage'));
const AccessManagementPage = lazy(() => import('../features/access/AccessManagementPage'));
const AuditLogPage = lazy(() => import('../features/audit/AuditLogPage'));
const InterOrganizationIdentityPage = lazy(() => import('../features/interOrganizationIdentity/InterOrganizationIdentityPage'));

/**
 * Every address the console can draw, resolved against one document's own address
 * rather than the browser's.
 *
 * The `location` override is what makes several documents possible at once: each copy
 * of this table matches the address of the tab it belongs to, so two tabs can sit side
 * by side on different journals and each one's `useParams`, `useLocation`, and search
 * parameters answer for itself. The browser bar still carries whichever tab is in front,
 * which is what keeps links, bookmarks, and the back button working as they always did.
 */
export function ConsoleDocumentRoutes({ address }: { address: string }) {
  return (
    <Routes location={address}>
      <Route path="" element={<DashboardPage />} />
      <Route path="experiences" element={<ExperienceCenterPage />} />
      <Route path="journals" element={<JournalsPage />} />
      <Route path="journals/new" element={<JournalEditorPage />} />
      <Route path="journals/:journalId" element={<JournalDetailPage />} />
      <Route path="journals/:journalId/edit" element={<JournalEditorPage />} />
      <Route path="ledger" element={<LedgerPage />} />
      <Route path="accounts" element={<AccountsPage />} />
      <Route path="persons" element={<PersonsPage />} />
      <Route path="customers" element={<CustomersPage />} />
      <Route path="suppliers" element={<SuppliersPage />} />
      <Route path="identity" element={<InterOrganizationIdentityPage />} />
      <Route path="currency/rates" element={<ExchangeRatesPage />} />
      <Route path="currency/administration" element={<CurrencyAdministrationPage />} />
      <Route path="inventory" element={<InventoryPage />} />
      <Route path="reports" element={<ReportsPage />} />
      <Route path="receivables" element={<ReceivablesPage />} />
      <Route path="payables" element={<PayablesPage />} />
      <Route path="treasury" element={<TreasuryPage />} />
      <Route path="sales" element={<SalesPage />} />
      <Route path="purchasing" element={<PurchasingPage />} />
      <Route path="automation" element={<DocumentAutomationPage />} />
      <Route path="payroll" element={<PayrollPage />} />
      <Route path="workforce" element={<WorkforcePage />} />
      <Route path="tax" element={<TaxPage />} />
      <Route path="fixed-assets" element={<FixedAssetsPage />} />
      <Route path="budgeting" element={<BudgetingPage />} />
      <Route path="costing" element={<CostingPage />} />
      <Route path="construction" element={<ConstructionOperationsPage />} />
      <Route path="projects" element={<ProjectsPage />} />
      <Route path="projects/:projectId" element={<ProjectsPage />} />
      <Route path="personal-finance" element={<PersonalFinancePage />} />
      <Route path="personal-finance/loans/:loanId" element={<PersonalFinancePage />} />
      <Route path="personal-finance/members/:memberId" element={<PersonalFinancePage />} />
      <Route path="personal-finance/expenses/:expenseId" element={<PersonalFinancePage />} />
      <Route path="settings" element={<SettingsPage />} />
      <Route path="settings/access" element={<AccessManagementPage />} />
      <Route path="settings/audit" element={<AuditLogPage />} />
      <Route path="*" element={<NotFoundPage />} />
    </Routes>
  );
}
